import { forwardRef, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

const base =
  'w-full rounded-md border bg-surface px-3 text-sm text-fg caret-primary shadow-xs transition-[border-color,box-shadow,background-color] duration-200 ease-(--ease-out) placeholder:text-fg-subtle placeholder:transition-opacity focus:placeholder:opacity-60 ' +
  'focus:outline-none focus:ring-4 disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-fg-muted read-only:bg-surface-2';
const ok = 'border-border-strong hover:border-primary/45 hover:bg-[color-mix(in_oklab,var(--color-primary)_2%,var(--color-surface))] focus:border-primary focus:bg-surface focus:ring-primary/14 focus:shadow-[0_1px_2px_rgb(0_0_0/0.04),0_6px_16px_-10px_color-mix(in_oklab,var(--color-primary)_45%,transparent)]';
// A field with a problem: red, and a small shake when the problem appears (motion settings apply).
const bad = 'fx-shake border-danger bg-danger-soft/30 focus:border-danger focus:ring-danger/15';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  ({ className, invalid, ...props }, ref) => (
    <input ref={ref} aria-invalid={invalid || undefined} className={cn(base, 'h-9', invalid ? bad : ok, className)} {...props} />
  ),
);
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  ({ className, invalid, ...props }, ref) => (
    <textarea ref={ref} aria-invalid={invalid || undefined} className={cn(base, 'min-h-20 max-h-48 resize-y py-2 [field-sizing:content]', invalid ? bad : ok, className)} {...props} />
  ),
);
Textarea.displayName = 'Textarea';

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }>(
  ({ className, invalid, children, ...props }, ref) => (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(base, 'h-9 appearance-none bg-[url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20viewBox%3D%270%200%2020%2020%27%20fill%3D%27%238a93a3%27%3E%3Cpath%20d%3D%27M5.5%207.5l4.5%204.5%204.5-4.5z%27/%3E%3C/svg%3E")] bg-[length:18px] bg-[right_8px_center] bg-no-repeat pr-8', invalid ? bad : ok, className)}
      {...props}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';
